import { useState, useMemo } from "react";
import { Users, User, Pencil, Search, ArrowUpDown, Eye, EyeOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roleLabels = { admin: "Gruppe 1 (Administrator)", editor: "Gruppe 2 (Redaktør)", member: "Gruppe 3 (Medlem)" };
const roleColors = { admin: "bg-purple-600/20 text-purple-400", editor: "bg-blue-600/20 text-blue-400", member: "bg-green-600/20 text-green-400" };
const roleOrder = { admin: 0, editor: 1, member: 2 };

const sortOptions = [
  { value: "name_asc", label: "Navn (A-Z)" },
  { value: "name_desc", label: "Navn (Z-A)" },
  { value: "role", label: "Rolle" },
  { value: "status", label: "Status" },
  { value: "last_login", label: "Sidst aktiv" },
  { value: "created", label: "Nyeste først" },
];

const MembersListTab = ({ members, loading, isAdmin, onEditMember, getAuthHeaders, onRefresh }) => {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");

  const deleteMember = async (e, memberName) => {
    e.stopPropagation();
    if (!window.confirm(`Er du sikker på du vil slette ${memberName}? Dette kan ikke fortrydes.`)) return;
    try {
      await axios.delete(`${API}/member-codes/${encodeURIComponent(memberName)}`, { headers: getAuthHeaders() });
      toast.success(`${memberName} er slettet`);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke slette");
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let list = members;

    if (q) {
      list = list.filter(m =>
        (m.member_name || "").toLowerCase().includes(q) ||
        (m.nickname || "").toLowerCase().includes(q) ||
        (m.email || "").toLowerCase().includes(q)
      );
    }

    return [...list].sort((a, b) => {
      switch (sortBy) {
        case "name_asc": return (a.member_name || "").localeCompare(b.member_name || "", "da");
        case "name_desc": return (b.member_name || "").localeCompare(a.member_name || "", "da");
        case "role": return (roleOrder[a.role] ?? 3) - (roleOrder[b.role] ?? 3);
        case "status": return (a.is_active === false ? 1 : 0) - (b.is_active === false ? 1 : 0);
        case "last_login": return new Date(b.last_login || 0) - new Date(a.last_login || 0);
        case "created": return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        default: return 0;
      }
    });
  }, [members, search, sortBy]);

  return (
    <div>
      <h2 className="text-2xl font-['Oswald'] font-bold text-white mb-4">ALLE MEDLEMMER</h2>

      {/* Search & Sort bar */}
      {!loading && members.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Søg efter navn, kaldenavn eller email..."
              className="mc-input w-full pl-10 pr-4 py-2.5"
              data-testid="members-search-input"
            />
          </div>
          <div className="relative">
            <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="mc-input pl-10 pr-8 py-2.5 appearance-none cursor-pointer min-w-[180px]"
              data-testid="members-sort-select"
            >
              {sortOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12"><div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : members.length === 0 ? (
        <div className="mc-card p-12 text-center">
          <Users className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <p className="text-white font-bold text-lg">Ingen medlemmer endnu</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="mc-card p-12 text-center" data-testid="members-no-results">
          <Search className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">Ingen medlemmer matcher "{search}"</p>
          <button onClick={() => setSearch("")} className="text-blue-400 hover:text-blue-300 text-sm mt-2">Ryd søgning</button>
        </div>
      ) : (
        <>
          <p className="text-gray-500 text-xs mb-3" data-testid="members-count">{filtered.length} af {members.length} medlemmer</p>
          <div className="space-y-3">
            {filtered.map((m) => (
              <div key={m.member_name} className={`mc-card p-5 ${m.is_active === false ? 'opacity-50' : ''} ${isAdmin ? 'cursor-pointer hover:border-[#00d4ff]/30 transition-all' : ''}`} data-testid={`member-card-${m.member_name}`} onClick={() => isAdmin && onEditMember(m)}>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {m.profile_image ? (
                      <img src={m.profile_image.startsWith("http") ? m.profile_image : `${process.env.REACT_APP_BACKEND_URL}${m.profile_image}`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User size={20} className="text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-lg font-bold text-white">{m.nickname || m.member_name}</h3>
                      {m.nickname && <span className="text-gray-400 text-sm italic ml-1">(Civilt kendt som: {m.member_name})</span>}
                      {isAdmin && <span className={`px-2 py-0.5 rounded text-xs ${roleColors[m.role] || roleColors.member}`}>
                        {roleLabels[m.role] || "Gruppe 3"}
                      </span>}
                      {m.is_active === false && <span className="px-2 py-0.5 rounded text-xs bg-red-600/20 text-red-400">Deaktiveret</span>}
                    {m.show_public === false && <span className="px-2 py-0.5 rounded text-xs bg-yellow-600/20 text-yellow-400 flex items-center gap-1"><EyeOff size={10} /> Skjult</span>}
                    {m.show_public !== false && m.is_active !== false && <span className="px-2 py-0.5 rounded text-xs bg-emerald-600/20 text-emerald-400 flex items-center gap-1"><Eye size={10} /> Offentlig</span>}
                    {m.display_order && <span className="px-2 py-0.5 rounded text-xs bg-gray-600/20 text-gray-400">#{m.display_order}</span>}
                    </div>
                    {m.email && <p className="text-gray-500 text-sm">{m.email}</p>}
                    {m.phone && (
                      <p className="text-gray-500 text-sm flex items-center gap-1.5">
                        {m.phone}
                        {m.phone_private && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-600/20 text-green-400">kun internt</span>}
                      </p>
                    )}
                    {m.bio && <p className="text-gray-600 text-xs mt-1 line-clamp-1">{m.bio}</p>}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Pencil size={16} className="text-gray-600" />
                      {m.code !== "__ADMIN__" && (
                        <button onClick={(e) => deleteMember(e, m.member_name)} className="p-1.5 rounded-lg text-gray-600 hover:text-red-400 hover:bg-red-500/10 transition-all" data-testid={`delete-member-${m.member_name}`} title="Slet medlem">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default MembersListTab;
